import {useEffect, useMemo, useState} from 'react';
import {statesAbbreviation} from 'definitions/States';
import {DirectBookingSchema} from 'definitions/Yup';
import {selectUserProfile} from 'features/User';
import {useLocale, useQuery} from 'hooks';
import {
  DirectBookingFormData,
  DirectBookingProviderType,
  SliceStatus,
} from 'interfaces';
import {useForm} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useParams} from 'react-router';
import {getLocaleFromCountryName} from 'utils';

import {yupResolver} from '@hookform/resolvers/yup';

import {cashPaymentOption, InsuranceStatus} from './constants';
import {getDirectBookingFilterOptions} from './directBookingActions';
import {selectDirectBookingOptions} from './directBookingSelectors';

const mapOption = (option: string) => ({
  label: option.charAt(0).toUpperCase() + option.slice(1),
  value: option,
});

function mapOptions(options?: string[]): {label: string; value: string}[] {
  return Array.isArray(options) ? options.map(mapOption) : [];
}

export type SelectOptions = {
  state?: string;
  paymentType?: string;
  conditions?: string[];
  profession?: string;
  country?: string;
  languages?: string[];
  ethnicity?: string[];
  education?: string;
  searchText?: string;
  acceptedInsurances?: string[];
} | null;

export const useDirectBookingFilter = (
  provider?: DirectBookingProviderType,
) => {
  const params = useParams<{[key: string]: string}>();
  const query = useQuery();
  const user = useSelector(selectUserProfile);
  const {localeOptions, setLocale} = useLocale();
  const history = useHistory();

  const currentLocale = useMemo(() => {
    const pathname = window.location.pathname;
    const pathComponents = pathname.split('/');
    const localeFromPath = pathComponents[1];
    const localeOption = localeOptions.find(
      option => option.value === localeFromPath,
    );
    return localeOption ?? {label: 'United States', value: 'en-us'};
  }, [localeOptions]);

  const userDefaultValues = useMemo(() => {
    if (!user) {
      return {};
    }

    const hasProfession =
      user.therapistDetails?.therapistId ||
      user.prescriberDetails?.prescriberId;

    return {
      profession: hasProfession
        ? user.therapistDetails?.therapistId
          ? 'Therapist'
          : 'Prescriber'
        : '',
      state:
        statesAbbreviation.find(state => state.label === user.stateOfResidence)
          ?.value ?? '',
    };
  }, [user]);

  const defaultValues = useMemo(() => {
    return {
      profession: query.get('profession') ?? userDefaultValues.profession ?? '',
      state: userDefaultValues.state ?? query.get('state') ?? '',
      conditions: query.get('conditions')
        ? query.get('conditions')?.split(',')
        : [],
      paymentType:
        params.insuranceStatus === InsuranceStatus.Uninsured
          ? cashPaymentOption.value
          : query.get('paymentType') ?? '',
      country: query.get('country') ?? currentLocale?.label ?? '',
      searchText: query.get('searchText') ?? '',
    };
  }, [userDefaultValues]);
  const [selectedOptions, setSelectedOptions] =
    useState<SelectOptions>(defaultValues);
  const dispatch = useDispatch();

  const {data: fetchedOptions, status} = useSelector(
    selectDirectBookingOptions,
  );

  useEffect(() => {
    if (!Object.keys(fetchedOptions).length) {
      dispatch(getDirectBookingFilterOptions());
    }
  }, []);

  const {control, watch, getValues, setValue, reset} = useForm<
    DirectBookingFormData,
    {}
  >({
    defaultValues,
    resolver: yupResolver(DirectBookingSchema),
  });

  const [allowInternalUpdate, setAllowInternalUpdate] = useState<boolean>(true);

  const watchSearchText = watch('searchText') ?? '';
  const [getDebouncedSearchText, setDebouncedSearchText] =
    useState<string>(watchSearchText);

  useEffect(() => {
    const handleSearchChange = setTimeout(
      () => setDebouncedSearchText(watchSearchText),
      2000,
    );

    return () => clearTimeout(handleSearchChange);
  }, [watchSearchText]);

  query.set('profession', watch('profession'));
  query.set('state', watch('state') ?? '');
  query.set('conditions', watch('conditions').join(','));
  query.set('paymentType', watch('paymentType'));
  query.set('country', watch('country') ?? '');
  query.set('searchText', watchSearchText ?? '');
  const urlParams = query.toString();

  const currentCountry = watch('country');

  useEffect(() => {
    if (
      currentCountry &&
      currentLocale &&
      currentLocale.label !== currentCountry
    ) {
      const newLocale = getLocaleFromCountryName(currentCountry);
      if (!newLocale) return;
      setLocale(newLocale);
      const localePattern = /\/[a-z]{2}-[a-z]{2}\b/g;
      const pathName = location.pathname.replace(localePattern, '');
      history.replace(`/${newLocale}${pathName}`);
    }
  }, [currentCountry]);

  const {
    paymentTypeOptions,
    professionOptions,
    stateOptions,
    conditionOptions,
  } = useMemo(() => {
    const selectedState = selectedOptions?.state;
    const selectedStateLabel = statesAbbreviation.find(
      sa => sa.value === selectedState,
    )?.label;

    let filteredInsurances = fetchedOptions.approvedInNetworkInsurances ?? [];
    let filteredStates = fetchedOptions?.state ?? [];

    // Don't allow to select any other states if the user is authenticated
    if (userDefaultValues.state) {
      filteredStates = filteredStates.filter(
        state => state === userDefaultValues.state,
      );
    }

    if (selectedState) {
      filteredInsurances = filteredInsurances.filter(i =>
        i.states.includes(selectedState),
      );
    }

    const filteredProfession = fetchedOptions?.profession ?? [];

    const approvedInsurances = mapOptions(
      filteredInsurances?.map(ins => ins.payerName) ?? [],
    );

    return {
      approvedInsurances,
      professionOptions: mapOptions(filteredProfession),
      conditionOptions: mapOptions(fetchedOptions.conditions),
      stateOptions:
        filteredStates.map(
          s =>
            statesAbbreviation.find(sa => sa.value === s) ?? {
              label: s,
              value: s,
            },
        ) ?? [],
      paymentTypeOptions: [
        {
          label: 'Payment type',
          value: InsuranceStatus.Uninsured,
          options: [cashPaymentOption],
        },
        {
          label: selectedStateLabel
            ? `Insurance (Available in ${selectedStateLabel})`
            : 'All Insurance',
          value: InsuranceStatus.Insured,
          options: provider?.acceptedInsurance
            ? approvedInsurances.filter(ai =>
                provider.acceptedInsurance?.some(
                  i => i.toLowerCase() === ai.value.toLowerCase(),
                ),
              )
            : approvedInsurances,
        },
      ],
    };
  }, [status, selectedOptions, provider, userDefaultValues]);

  useEffect(() => {
    if (!allowInternalUpdate) return;

    const values = getValues();
    setSelectedOptions({
      state: values.state,
      paymentType: values.paymentType,
      country: values.country,
      conditions: values.conditions.filter(c => !!c) as string[],
      profession: values.profession,
      languages: values.languages?.filter(c => !!c) as string[],
      ethnicity: values.ethnicity?.filter(c => !!c) as string[],
      education: values.education,
      searchText: getDebouncedSearchText,
    });
  }, [getDebouncedSearchText, urlParams, status]);

  useEffect(() => {}, [selectedOptions]);

  return {
    user,
    control,
    urlParams,
    paymentTypeOptions,
    professionOptions,
    stateOptions,
    conditionOptions,
    isLoadingOptions: status === SliceStatus.pending,
    selectedOptions,
    setValue,
    getValues,
    reset,
    allowInternalUpdate,
    setAllowInternalUpdate,
    setSelectedOptions,
  };
};
